import React from 'react';
import { Link } from 'react-router-dom';
import { Tag } from 'lucide-react';
import { motion } from 'framer-motion';

/**
 * ArticleTags Component
 * Displays clickable tags that link to filtered blog views
 * Improves internal interlinking and navigation
 */
export const ArticleTags = ({ tags, variant = 'default' }) => {
    if (!tags || tags.length === 0) return null;

    // Convert tag slug to display name
    const formatTagName = (tag) => {
        return tag
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Styles based on variant
    const containerStyles = {
        default: 'flex flex-wrap items-center gap-2',
        compact: 'flex flex-wrap items-center gap-1.5',
        featured: 'flex flex-wrap items-center gap-3'
    };

    const tagStyles = {
        default: 'px-3 py-1.5 text-xs font-medium bg-white/5 border border-white/10 rounded-full text-muted-foreground hover:text-primary hover:border-primary/30 hover:bg-primary/10 transition-all duration-200',
        compact: 'px-2 py-1 text-xs font-medium bg-white/5 border border-white/10 rounded text-muted-foreground hover:text-primary hover:border-primary/30 transition-all duration-200',
        featured: 'px-4 py-2 text-sm font-medium bg-primary/10 border border-primary/20 rounded-full text-primary hover:bg-primary/20 hover:border-primary/40 transition-all duration-200'
    };

    return (
        <div className={containerStyles[variant]}>
            <span className="flex items-center gap-1.5 text-muted-foreground mr-2">
                <Tag className="w-4 h-4" />
                <span className="text-sm font-medium">Tags:</span>
            </span>
            {tags.map((tag, index) => (
                <motion.div
                    key={tag}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2, delay: index * 0.05 }}
                >
                    <Link
                        to={`/blog?tag=${encodeURIComponent(tag)}`}
                        className={tagStyles[variant]}
                    >
                        #{formatTagName(tag)}
                    </Link>
                </motion.div>
            ))}
        </div>
    );
};

/**
 * InlineTagLinks Component
 * For use within article content - more subtle styling
 */
export const InlineTagLinks = ({ tags }) => {
    if (!tags || tags.length === 0) return null;

    const formatTagName = (tag) => {
        return tag
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    return (
        <div className="flex flex-wrap gap-2 mt-4">
            {tags.slice(0, 5).map((tag) => (
                <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="text-primary/80 hover:text-primary text-sm underline-offset-2 hover:underline transition"
                >
                    #{formatTagName(tag)}
                </Link>
            ))}
        </div>
    );
};

/**
 * RelatedByTags Component
 * Shows articles that share common tags
 */
/**
 * RelatedByTags Component
 * "Smart Read Next" Algorithm
 * Shows articles based on weighted relevance:
 * - Same Category: +5 points
 * - Matching Tag: +3 points per tag
 * - Title Keyword Match: +1 point (To be implemented if needed)
 */
export const RelatedByTags = ({ currentArticle, allArticles, maxItems = 4 }) => {
    if (!currentArticle || !allArticles || allArticles.length === 0) return null;

    // Filter and Score
    const relatedArticles = allArticles
        .filter(article => article.id !== currentArticle.id) // Exclude self
        .map(article => {
            let score = 0;

            // 1. Category Match (+5)
            if (article.category === currentArticle.category) {
                score += 5;
            }

            // 2. Tag Intersection (+3 per match)
            if (article.tags && currentArticle.tags) {
                const intersection = article.tags.filter(tag =>
                    currentArticle.tags.includes(tag)
                );
                score += (intersection.length * 3);
            }

            return { ...article, score };
        })
        .filter(a => a.score > 0) // Only relevant items
        .sort((a, b) => b.score - a.score) // Highest score first
        .slice(0, maxItems);

    if (relatedArticles.length === 0) return null;

    return (
        <div className="my-8 p-6 glass rounded-xl border border-white/10">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5 text-primary" />
                Related Articles
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {relatedArticles.map((article) => (
                    <Link
                        key={article.id}
                        to={article.canonicalPath || `/blog/${article.slug}`}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/5 transition group"
                    >
                        <span className="text-primary group-hover:translate-x-1 transition">→</span>
                        <div>
                            <h4 className="font-medium text-sm group-hover:text-primary transition line-clamp-2">
                                {article.title}
                            </h4>
                            <div className="flex gap-1 mt-1">
                                <span className="text-xs text-muted-foreground bg-white/5 px-2 py-0.5 rounded">
                                    {article.category}
                                </span>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
};
